import { FC } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isNil } from 'lodash';
import numeral from 'numeral';
import { DailyNavBreakdownCompRow } from 'api/models/DailyNavBreakdownCompRow';
import { DailyNavBreakdownMetric } from 'api/models/DailyNavBreakdownMetric';
import { DailyNavBreakdownWindowSummary } from 'api/models/DailyNavBreakdownWindowSummary';
import { shortDate } from 'utilities';
import { DeltaBreakdownDialogProps } from './DeltaBreakdownDialog.props';

// Mirrors the DeltaCell/ReviewPriceDialog signed-percent format exactly.
const formatSignedPercent = (value: string | null | undefined) => {
  if (isNil(value)) {
    return '—';
  }
  const numericValue = Number(value);
  return `${numericValue > 0 ? '+' : ''}${(numericValue * 100).toFixed(1)}%`;
};

// Symbols for the currencies comps actually report in; anything else falls back
// to an ISO prefix ("CHF 12.30"), which is unambiguous if less pretty.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
};

// The server scales values to absolute quantities using the metric's catalog
// factor, so nothing is scaled here. `unit` picks the shape: ratio -> percent,
// multiple -> "1.20x", count -> plain integer, currency -> the comp's own
// reported currency. Small magnitudes get more decimals so a sub-cent price
// never collapses to a misleading "$0.00".
const formatMetricValue = (
  value: string | null | undefined,
  metric: DailyNavBreakdownMetric | undefined,
  currency: string | null | undefined
) => {
  if (isNil(value)) {
    return '—';
  }
  const numericValue = Number(value);
  if (metric?.unit === 'ratio') {
    return `${(numericValue * 100).toFixed(1)}%`;
  }
  if (metric?.unit === 'multiple') {
    return `${numeral(numericValue).format('0,0.00')}x`;
  }
  if (metric?.unit === 'count') {
    return numeral(numericValue).format('0,0');
  }
  // Alexandria reports each comp's own listing currency; fall back to a bare
  // number rather than implying dollars when it sends none.
  const symbol = CURRENCY_SYMBOLS[currency ?? ''] ?? (currency ? `${currency} ` : '');
  const magnitude = Math.abs(numericValue);
  if (numericValue !== 0 && magnitude < 0.01) {
    return `${symbol}${numeral(numericValue).format('0,0.000000')}`;
  }
  if (magnitude < 1) {
    return `${symbol}${numeral(numericValue).format('0,0.0000')}`;
  }
  if (magnitude >= 1e6) {
    return `${symbol}${numeral(numericValue).format('0.00a')}`;
  }
  return `${symbol}${numeral(numericValue).format('0,0.00')}`;
};

const formatThreshold = (summary: DailyNavBreakdownWindowSummary) => {
  if (isNil(summary.threshold) || summary.threshold_source === 'none') {
    return '—';
  }
  const effective = `${(Number(summary.threshold) * 100).toFixed(1)}%`;
  // An override is only judgeable against what it departed from, and `threshold` is
  // already the effective value -- so the firm default comes from its own field.
  if (summary.threshold_source === 'company' && !isNil(summary.firm_threshold)) {
    return `${effective} (company override; firm default ${(Number(summary.firm_threshold) * 100).toFixed(1)}%)`;
  }
  return `${effective} (${summary.threshold_source})`;
};

// Reasons a comp is excluded from the average, humanized for display.
const EXCLUDED_REASON_LABELS: Record<string, string> = {
  no_ticker: 'no ticker',
  unresolved: 'unresolved in CapIQ',
  no_data: 'no market data',
};

const humanizeExcludedReason = (reason: string) =>
  EXCLUDED_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');

const compLabel = (comp: DailyNavBreakdownCompRow) =>
  comp.ticker ? `${comp.company_name} (${comp.ticker})` : comp.company_name;

const DIVIDER_ROW_SX = { borderTop: 2, borderColor: 'divider' };

// What the server did to the source values, when it is worth saying. A factor
// of 1 changed nothing, so "×1" would be noise; any other factor materially
// moved the numbers and belongs on screen. A null factor is a different
// statement — the catalog could not be read, so the magnitude is unknown.
const scalingNote = (metric: DailyNavBreakdownMetric | undefined) => {
  if (!metric) {
    return null;
  }
  if (isNil(metric.factor)) {
    return {
      label: '(unscaled)',
      title: 'Magnitude unknown — showing the raw value as reported.',
    };
  }
  const factor = Number(metric.factor);
  if (factor === 1) {
    return null;
  }
  const formatted = numeral(factor).format('0,0');
  return {
    label: `(×${formatted})`,
    title: `Source values scaled by ${formatted} to an absolute quantity.`,
  };
};

// Alexandria substitutes the last available observation when a date has none,
// so a differing as_of_date means the number is a carried-forward quote, not a
// reading from the date we asked for. Worth saying on an audit surface.
const staleNote = (requested?: string | null, asOf?: string | null) =>
  asOf && requested && asOf !== requested ? ` (as of ${shortDate(asOf) ?? asOf})` : '';

// Signed percent, with a tooltip showing the two values and their dates.
interface WindowPctCellProps {
  pct: string | null;
  excluded: boolean;
  currentValue: string | null;
  currentDate?: string;
  priorValue: string | null;
  priorDate?: string | null;
  metric: DailyNavBreakdownMetric | undefined;
  currency: string | null;
  currentAsOfDate: string | null;
  priorAsOfDate: string | null;
}

const WindowPctCell: FC<WindowPctCellProps> = ({
  pct,
  excluded,
  currentValue,
  currentDate,
  priorValue,
  priorDate,
  metric,
  currency,
  currentAsOfDate,
  priorAsOfDate,
}) => {
  if (excluded || isNil(pct)) {
    return (
      <TableCell align="right" sx={excluded ? { color: 'text.disabled' } : undefined}>
        —
      </TableCell>
    );
  }
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <div>
            {`${shortDate(priorDate ?? undefined) ?? '—'}: ${formatMetricValue(priorValue, metric, currency)}`}
            {staleNote(priorDate, priorAsOfDate)}
          </div>
          <div>
            {`${shortDate(currentDate) ?? '—'}: ${formatMetricValue(currentValue, metric, currency)}`}
            {staleNote(currentDate, currentAsOfDate)}
          </div>
        </Box>
      }>
      <TableCell align="right" sx={{ cursor: 'help' }}>
        {formatSignedPercent(pct)}
      </TableCell>
    </Tooltip>
  );
};

const DeltaBreakdownDialog: FC<DeltaBreakdownDialogProps> = ({ row, open, onClose }) => {
  // The breakdown is delivered inline on each record by the list endpoint, so
  // there is nothing to fetch — the dialog opens with no extra request.
  const data = row?.record.breakdown;
  const dates = data?.dates;

  return (
    <Dialog id="delta-breakdown-dialog" data-testid="delta-breakdown-dialog" open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`${row?.companyName ?? ''} — Delta Breakdown`}</DialogTitle>
      <DialogContent>
        {dates && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', marginBottom: 1.5 }}>
            {`Current: ${shortDate(dates.current) ?? '—'} · T-${data?.short_window_sessions ?? '?'}: ${
              shortDate(dates.prior_short_window) ?? '—'
            } · T-${data?.long_window_sessions ?? '?'}: ${
              shortDate(dates.prior_long_window) ?? '—'
            } · Last Valuation: ${dates.last_valuation ? shortDate(dates.last_valuation) ?? '—' : '—'}`}
          </Typography>
        )}

        {/* The row is read as of the report date's market close, and every
            window date is read at that same instant -- so these figures are what
            was known then, not what is known now. Sources publish after the
            close, so a value can legitimately be the previous session's; the
            per-cell "as of" dates say which. Stated up front because a reader
            comparing against a live quote will otherwise think it is wrong. */}
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', marginBottom: 1.5 }}>
            Values are as reported at market close on the report date. A source that publishes after the close may
            still be showing the previous session — each row&apos;s &ldquo;as of&rdquo; date shows which.
        </Typography>

        {!data && (
          <Typography variant="body2" sx={{ color: 'text.secondary', paddingY: 2 }}>
            No breakdown data available.
          </Typography>
        )}

        {data && (
          <Table id="delta-breakdown-table" data-testid="delta-breakdown-table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Comp</TableCell>
                <TableCell align="right">
                  {data.metric?.display_name ?? 'Value'}
                  {scalingNote(data.metric) && (
                    <Tooltip arrow title={scalingNote(data.metric)?.title ?? ''}>
                      <Typography component="span" variant="caption" sx={{ color: 'text.disabled', marginLeft: 0.5, cursor: 'help' }}>
                        {scalingNote(data.metric)?.label}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">{`${data.short_window_sessions}-Session`}</TableCell>
                <TableCell align="right">{`${data.long_window_sessions}-Session`}</TableCell>
                <TableCell align="right">Since Last Val</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.comps.map(comp => {
                const excluded = !isNil(comp.excluded_reason);
                const rowSx = excluded ? { color: 'text.disabled' } : {};
                return (
                  <TableRow key={`${comp.company_name}-${comp.ticker ?? comp.comp_type}`}>
                    <TableCell sx={rowSx}>
                      {compLabel(comp)}
                      {excluded && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.disabled', marginLeft: 0.5 }}>
                          {`(${humanizeExcludedReason(comp.excluded_reason as string)})`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={rowSx}>
                      {formatMetricValue(comp.current_value, data.metric, comp.currency)}
                    </TableCell>
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.short_window.pct}
                      currentValue={comp.current_value}
                      currentDate={data.dates.current}
                      priorValue={comp.short_window.prior_value}
                      priorDate={data.dates.prior_short_window}
                      priorAsOfDate={comp.short_window.prior_as_of_date}
                      metric={data.metric}
                      currency={comp.currency}
                      currentAsOfDate={comp.current_as_of_date}
                    />
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.long_window.pct}
                      currentValue={comp.current_value}
                      currentDate={data.dates.current}
                      priorValue={comp.long_window.prior_value}
                      priorDate={data.dates.prior_long_window}
                      priorAsOfDate={comp.long_window.prior_as_of_date}
                      metric={data.metric}
                      currency={comp.currency}
                      currentAsOfDate={comp.current_as_of_date}
                    />
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.since_last_valuation.pct}
                      currentValue={comp.current_value}
                      currentDate={data.dates.current}
                      priorValue={comp.since_last_valuation.prior_value}
                      priorDate={data.dates.last_valuation}
                      priorAsOfDate={comp.since_last_valuation.prior_as_of_date}
                      metric={data.metric}
                      currency={comp.currency}
                      currentAsOfDate={comp.current_as_of_date}
                    />
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell sx={{ ...DIVIDER_ROW_SX, fontWeight: 600 }}>Average</TableCell>
                <TableCell sx={DIVIDER_ROW_SX} />
                {/* A breached average reads red rather than carrying a separate
                    status row -- the number and its verdict are the same fact,
                    and splitting them made the reader join two rows to learn one
                    thing. Matches SecondaryBreakdownDialog. */}
                {[data.short_window, data.long_window, data.since_last_valuation].map((summary, index) => (
                  <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    align="right"
                    sx={{ ...DIVIDER_ROW_SX, fontWeight: 600, color: summary.exceeded ? 'error.main' : 'inherit' }}>
                    {formatSignedPercent(summary.average)}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                <TableCell>Threshold</TableCell>
                <TableCell />
                <TableCell align="right">{formatThreshold(data.short_window)}</TableCell>
                <TableCell align="right">{formatThreshold(data.long_window)}</TableCell>
                <TableCell align="right">{formatThreshold(data.since_last_valuation)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} id="delta-breakdown-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeltaBreakdownDialog;
